import React, { useState } from "react";
import { Plug, Wrench, Zap } from "lucide-react";

import { Breadcrumb } from "@/shared/components/Breadcrumb";
import { BrandButton, PageHeader } from "@/shared/components/ui-kit";
import { AppLogo } from "@/shared/components/AppLogo";
import { useApp, useConnectApp } from "@/shared/api/apps";
import { useSettings } from "@/shared/api/settings";
import { AUTH_LABEL } from "./components/AppCard";
import { ConnectionRow } from "./components/ConnectionRow";
import { ToolRow } from "./components/ToolRow";
import { TriggerRow } from "./components/TriggerRow";

/* One app, with everything `GET /apps/{slug}` carries: the spec the model will
   be handed, and the accounts I can see on it. The list route never ships the
   spec (github alone is 823 tool schemas), so this page is the only place it
   is read — one query, no prefetch from the grid. */
export function AppDetail({ slug, onBack }: { slug: string; onBack: () => void }) {
  const { data: app, isPending } = useApp(slug);
  const { data: settings } = useSettings();
  const { connect, busySlug } = useConnectApp();
  const [tab, setTab] = useState("tools");

  // `apps.share` is an admin permission (CONTRACT §6); owner holds every one an
  // admin does.
  const canShare = settings?.org?.role === "owner" || settings?.org?.role === "admin";

  if (!app) {
    return (
      <div className="flex-1 flex items-center justify-center text-[13px] text-[var(--text-tertiary)]">
        {isPending ? "Loading…" : "This app is not in the catalog."}
      </div>
    );
  }

  // The catalog ships `spec`; `connections` waits on the connections route.
  const tools = app.spec?.tools ?? [];
  const triggers = app.spec?.triggers ?? [];
  const connections = app.connections ?? [];

  const tabs = [
    { id: "tools", label: `Tools · ${tools.length}`, icon: Wrench },
    ...(triggers.length
      ? [{ id: "triggers", label: `Triggers · ${triggers.length}`, icon: Zap }]
      : []),
  ];

  return (
    <>
      <PageHeader
        title={
          <Breadcrumb parent={{ label: "Apps", onClick: onBack }}>
            <AppLogo logo={app.logo} name={app.name} size={20} iconSize={12} rounded={5} />
            <span className="truncate">{app.name}</span>
          </Breadcrumb>
        }
      />

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[880px] mx-auto px-4 md:px-6 py-6 space-y-4 fade-in-up">
          <div className="flex items-start gap-4">
            <AppLogo logo={app.logo} name={app.name} size={48} iconSize={26} rounded={12} />
            <div className="min-w-0 flex-1">
              <h1 className="text-[17px] font-semibold tracking-[-0.01em] m-0">{app.name}</h1>
              <p className="text-[13px] leading-[1.6] text-[var(--text-secondary)] mt-1 mb-0">
                {app.description}
              </p>
              <div className="flex items-center gap-3 mt-2 text-[11.5px] text-[var(--text-tertiary)]">
                <span>{app.category ?? "Other"}</span>
                <span className="w-[3px] h-[3px] rounded-full bg-[var(--text-quaternary)]" />
                <span>{AUTH_LABEL[app.auth]}</span>
                <span className="w-[3px] h-[3px] rounded-full bg-[var(--text-quaternary)]" />
                <span>{app.tool_count} {app.tool_count === 1 ? "tool" : "tools"}</span>
                {app.trigger_count > 0 && (
                  <>
                    <span className="w-[3px] h-[3px] rounded-full bg-[var(--text-quaternary)]" />
                    <span>{app.trigger_count} {app.trigger_count === 1 ? "trigger" : "triggers"}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center rounded-[9px] bg-[var(--surface-sunk)] ring-1 ring-[var(--hairline)] p-[3px] w-fit">
            {tabs.map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`press flex items-center gap-1.5 px-3 py-[4px] rounded-[7px] text-[12.5px] font-medium tabular-nums ${
                  tab === t.id
                    ? "bg-[var(--surface)] text-[var(--text-primary)] shadow-[0_1px_2px_rgba(0,0,0,0.03)] ring-1 ring-[var(--hairline)]"
                    : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"
                }`}
              >
                <t.icon className="w-3 h-3" strokeWidth={1.75} />
                {t.label}
              </button>
            ))}
          </div>

          <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] overflow-hidden">
            {tab === "tools"
              ? tools.map((t) => <ToolRow key={t.slug} tool={t} />)
              : triggers.map((t) => <TriggerRow key={t.slug} trigger={t} />)}
          </div>

          {tab === "triggers" && (
            <p className="text-[12px] text-[var(--text-tertiary)] leading-[1.6] m-0 flex items-start gap-1.5">
              <Plug className="w-3 h-3 mt-[2px] shrink-0" strokeWidth={1.75} />
              Subscribe from an agent&apos;s Settings — a subscription carries that agent&apos;s
              own filter and its own prompt, so it belongs to the agent, not to the catalog.
            </p>
          )}

          <div className="flex items-center gap-2.5">
            <div className="section-label">Connections</div>
            <div className="flex-1" />
            {app.auth === "none" ? (
              <span className="text-[12.5px] text-[var(--text-tertiary)]">No connection needed</span>
            ) : (
              <BrandButton
                onClick={() => connect(app.slug)}
                disabled={!!busySlug}
                className="disabled:opacity-40 disabled:pointer-events-none"
              >
                {busySlug === app.slug ? "Connecting…" : connections.length ? "Connect another" : "Connect"}
              </BrandButton>
            )}
          </div>

          <div className="rounded-[14px] bg-[var(--surface)] ring-1 ring-[var(--hairline)] overflow-hidden">
            {connections.length ? (
              connections.map((c) => <ConnectionRow key={c.id} connection={c} canShare={!!canShare} />)
            ) : (
              <div className="px-4 py-3 text-[12.5px] text-[var(--text-quaternary)] italic">
                {app.auth === "none"
                  ? `${app.name} works without an account — link it to an agent from its Settings.`
                  : "No accounts connected yet."}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
